import { Button, buttonVariants } from '@/components/ui/button';
import { Eyebrow, Label } from '@/components/enc/primitives';
import { PublicFooterBar, PublicHeader } from '@/components/enc/public-chrome';
import {
  RETREAT_PLACE,
  RETREAT_PLACE_DETAIL,
  retreatInfo,
} from '@/components/enc/retreat';
import { date } from '@/lib/date';
import { printFicha } from '@/lib/print/print-actions';
import { usePrintPreview } from '@/lib/print/use-print-preview';
import { cn } from '@/lib/utils';
import { useEvent } from '@/services/events';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  ChevronDown,
  MapPin,
  MessageSquare,
  Users,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  ['#retiro', 'O Retiro'],
  ['#vivencia', 'O que vamos viver'],
  ['#inspiracao', 'Inspiração'],
  ['#depoimentos', 'Depoimentos'],
  ['#duvidas', 'Dúvidas'],
] as const;

const TESTIMONIALS = [
  {
    quote: 'O retiro transformou nosso casamento. Foi uma experiência abençoada.',
    name: 'Ana e João',
    initials: 'AJ',
  },
  {
    quote: 'Saímos renovados e mais unidos. Foi maravilhoso!',
    name: 'Carla e Marcos',
    initials: 'CM',
  },
  {
    quote: 'Uma bênção para a nossa vida. Recomendamos a todos os casais.',
    name: 'Pedro e Laura',
    initials: 'PL',
  },
];

const AUDIENCE = [
  ['Recém-casados', 'Que querem começar a vida a dois com fundamentos firmes.'],
  [
    'Casais com anos de caminhada',
    'Que sentem a rotina pesar e desejam reacender o primeiro amor.',
  ],
  [
    'Casais em momento difícil',
    'Que buscam cura, perdão e um recomeço com a ajuda de Deus.',
  ],
  [
    'Casados ou não na Igreja',
    'O retiro também é um caminho para quem deseja receber o sacramento.',
  ],
] as const;

const H2 =
  'm-0 font-display text-[clamp(30px,3.6vw,44px)] leading-[1.1] font-extrabold tracking-[-0.03em] text-balance text-ink';
const P = 'm-0 text-base leading-[1.7] text-pretty text-[#4A4A4F]';

const pad = (n: number) => String(n).padStart(2, '0');

function useCountdown(target?: number) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, (target ?? now) - now);
  return {
    d: pad(Math.floor(diff / 864e5)),
    h: pad(Math.floor(diff / 36e5) % 24),
    m: pad(Math.floor(diff / 6e4) % 60),
  };
}

function StripItem({
  icon,
  label,
  title,
  sub,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="flex items-start gap-3.5 border-[rgba(11,11,12,.08)] px-6 py-5 min-[460px]:border-r">
      <span className="flex text-brand [&_svg]:size-5">{icon}</span>
      <div className="flex flex-col gap-1">
        <Label>{label}</Label>
        <span className="font-display text-[17px] font-bold text-ink">{title}</span>
        <span className="text-[13px] text-[#4A4A4F]">{sub}</span>
      </div>
    </div>
  );
}

const Page = () => {
  const { data: nextEvent } = useEvent('next');
  const info = retreatInfo(nextEvent);
  const cd = useCountdown(info.start?.valueOf());
  const [openFaq, setOpenFaq] = useState(0);
  const print = usePrintPreview();

  const faq: [string, string][] = [
    [
      'Precisamos ser casados na Igreja para participar?',
      'Não. O retiro é aberto a todos os casais. Para quem ainda não recebeu o sacramento do matrimônio, ele pode ser um primeiro passo nesse caminho.',
    ],
    [
      'Onde acontece o retiro?',
      "No Jardim da Auxiliadora, Bairro Santo Antônio do Sapezeiro, em Santa Bárbara d'Oeste - SP.",
    ],
    [
      'Quando será o próximo retiro?',
      info.hasEvent
        ? `Nos dias ${info.rangeLong}, ${info.weekdays?.toLowerCase()}.`
        : 'As datas do próximo retiro serão divulgadas em breve.',
    ],
    [
      'Precisamos participar juntos?',
      'Sim. O Enchei-vos é vivido a dois: a inscrição é feita pelo casal e todas as atividades são pensadas para os esposos.',
    ],
    [
      'Posso levar meus filhos?',
      'Sim. Durante o retiro, as crianças ficam no Cantinho da Criança, acolhidas por uma equipe dedicada, com brincadeiras, dinâmicas e momentos de evangelização pensados para a idade delas. Assim, vocês vivem o retiro com tranquilidade, sabendo que os pequenos também estão sendo cuidados.',
    ],
    [
      'Quais dados são pedidos na inscrição?',
      'Nome e telefone dos dois, e-mail, endereço, situação de batismo e crisma, paróquia, pastoral ou movimento e informações sobre os filhos.',
    ],
  ];

  const jsonLd = nextEvent && {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Retiro de Casais Enchei-vos',
    startDate: date(nextEvent.startDate).format('YYYY-MM-DD'),
    endDate: date(nextEvent.endDate).format('YYYY-MM-DD'),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: RETREAT_PLACE,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Bairro Santo Antônio do Sapezeiro',
        addressLocality: "Santa Bárbara d'Oeste",
        addressRegion: 'SP',
        addressCountry: 'BR',
      },
    },
    organizer: { '@type': 'Organization', name: 'Comunidade Católica Apóstolos' },
    description:
      'Retiro para casais com oração, pregações, partilhas e convivência fraterna, inspirado em São Luís e Santa Zélia Martin.',
  };

  return (
    <div className="min-h-screen bg-white font-[family-name:var(--font-ui)] text-body">
      <Helmet>
        <title>
          Retiro de Casais Enchei-vos | Comunidade Católica Apóstolos · Santa Bárbara
          d'Oeste
        </title>
        <meta
          name="description"
          content={`Retiro de Casais Enchei-vos: um fim de semana de oração, pregações e partilhas para renovar o casamento à luz de São Luís e Santa Zélia Martin.${info.hasEvent ? ` ${info.rangeLong} em Santa Bárbara d'Oeste - SP.` : ''}`}
        />
        <meta
          property="og:title"
          content={`Retiro de Casais Enchei-vos${info.hasEvent ? ` — ${info.rangeLong}` : ''}`}
        />
        <meta
          property="og:description"
          content="Dois dias para reacender o amor que Deus uniu. Inscrições abertas."
        />
        <meta property="og:image" content="https://encheivos.onebytedigital.com.br/assets/og-image-v2.jpg" />
        <link rel="canonical" href="https://encheivos.onebytedigital.com.br/" />
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        <style>{'html{scroll-behavior:smooth}'}</style>
      </Helmet>

      <PublicHeader homeHref="/#inicio">
        <div className="flex items-center gap-7 text-sm font-medium max-[959px]:hidden">
          {NAV_LINKS.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="text-body transition-colors hover:text-brand"
            >
              {label}
            </a>
          ))}
        </div>
        <Link className={buttonVariants({ size: 'sm' })} to="/inscricao">
          Quero me inscrever
        </Link>
      </PublicHeader>

      {/* HERO */}
      <section id="inicio" className="relative overflow-hidden">
        <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-6 pt-14 pb-10 [grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr))]">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2.5">
              <span className="size-2 rounded-full bg-[#3ECF8E]" />
              <Eyebrow>
                {info.hasEvent
                  ? `INSCRIÇÕES ABERTAS • ${info.rangeLong.toUpperCase()}`
                  : 'RETIRO DE CASAIS • INSCRIÇÕES EM BREVE'}
              </Eyebrow>
            </div>
            <h1 className="m-0 font-display text-[clamp(40px,5.6vw,72px)] leading-[1.04] font-extrabold tracking-[-0.035em] text-balance text-ink">
              Um fim de semana para reacender o amor que Deus uniu.
            </h1>
            <p className="m-0 max-w-[520px] text-lg leading-relaxed text-pretty text-[#4A4A4F]">
              O{' '}
              <strong className="font-semibold text-ink">
                Retiro de Casais Enchei-vos
              </strong>{' '}
              é um convite para pausar a rotina, olhar um para o outro e colocar Deus de
              volta no centro do casamento.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link className={buttonVariants({ size: 'lg' })} to="/inscricao">
                Garantir nossa vaga
              </Link>
              <a
                className={buttonVariants({ size: 'lg', variant: 'outline' })}
                href="#retiro"
              >
                Conhecer o retiro
              </a>
            </div>
            <figure className="m-0 mt-2 flex flex-col gap-1.5 border-l border-[rgba(11,11,12,.16)] pl-[18px]">
              <blockquote className="m-0 text-[15px] leading-normal text-body italic">
                "O amor tudo desculpa, tudo crê, tudo espera, tudo suporta."
              </blockquote>
              <figcaption className="text-xs font-semibold text-muted">
                1 Coríntios 13,7
              </figcaption>
            </figure>
          </div>
          <div className="relative flex justify-center overflow-visible">
            <img
              src="/assets/bg-top-mobile.png"
              alt="Pintura de São Luís e Santa Zélia Martin, padroeiros do Retiro de Casais Enchei-vos"
              className="block h-auto w-full max-w-[520px] object-contain [mask-composite:intersect] [mask-image:linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent),linear-gradient(to_bottom,transparent,#000_6%,#000_94%,transparent)] [-webkit-mask-composite:source-in] [-webkit-mask-image:linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent),linear-gradient(to_bottom,transparent,#000_6%,#000_94%,transparent)]"
            />
            <div className="absolute bottom-6 left-0 flex flex-col gap-1 rounded-[14px] border border-[rgba(11,11,12,.12)] bg-white px-[18px] py-3.5 shadow-[0_10px_30px_rgba(11,11,12,.08)]">
              <span className="text-[10px] font-bold tracking-[.12em] text-muted">
                INSPIRADOS POR
              </span>
              <span className="font-display text-base font-bold text-ink">
                São Luís e Santa Zélia Martin
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1200px] px-6 pb-14">
          <div className="grid overflow-hidden rounded-[14px] border border-[rgba(11,11,12,.12)] [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <StripItem
              icon={<Calendar />}
              label="QUANDO"
              title={info.whenTitle}
              sub={info.whenSub}
            />
            <StripItem
              icon={<MapPin />}
              label="ONDE"
              title={RETREAT_PLACE}
              sub={RETREAT_PLACE_DETAIL}
            />
            <StripItem
              icon={<Users />}
              label="PARA QUEM"
              title="Casais de todas as fases"
              sub="Recém-casados ou com décadas de união"
            />
            {info.hasEvent && (
              <div className="flex flex-col gap-2 bg-[#FCF4F3] px-6 py-5">
                <Eyebrow>FALTAM</Eyebrow>
                <div className="flex items-baseline gap-4">
                  {(
                    [
                      [cd.d, 'dias'],
                      [cd.h, 'horas'],
                      [cd.m, 'min'],
                    ] as const
                  ).map(([v, l]) => (
                    <div key={l} className="flex flex-col">
                      <span className="font-display text-[28px] leading-none font-extrabold text-ink">
                        {v}
                      </span>
                      <span className="text-[11px] text-muted">{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* O RETIRO */}
      <section id="retiro" className="border-t border-[rgba(11,11,12,.06)] bg-[#F6F6F4]">
        <div className="mx-auto grid max-w-[1200px] items-center gap-14 px-6 py-24 [grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr))]">
          <div className="grid grid-cols-2 grid-rows-[200px_200px] gap-3">
            <img
              src="/assets/about-1.jpg"
              alt="Casal em adoração diante do Santíssimo no Retiro Enchei-vos"
              className="row-span-2 h-full w-full rounded-[14px] object-cover"
            />
            <img
              src="/assets/about-2.jpg"
              alt="Casais assistindo à pregação durante o retiro"
              className="h-full w-full rounded-[14px] object-cover"
            />
            <img
              src="/assets/about-3.jpg"
              alt="Casal participante do Retiro de Casais Enchei-vos"
              className="h-full w-full rounded-[14px] object-cover"
            />
          </div>
          <div className="flex flex-col gap-5">
            <Eyebrow>O RETIRO</Eyebrow>
            <h2 className={H2}>A santidade também se vive em casa, a dois.</h2>
            <p className={P}>
              Realizado pela{' '}
              <strong className="font-semibold text-ink">
                Comunidade Católica Apóstolos
              </strong>
              , o Enchei-vos ajuda os casais a redescobrirem a beleza e a profundidade do
              sacramento do matrimônio. Em um mundo onde tantos casamentos enfrentam
              desafios, acreditamos que o amor pode ser renovado quando o casal coloca
              Deus no centro da sua vida.
            </p>
            <p className={P}>
              Ao longo dos encontros já testemunhamos relacionamentos restaurados,
              famílias fortalecidas e uma nova experiência de fé vivida a dois.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {['Cura', 'Reconciliação', 'Crescimento espiritual'].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-[rgba(11,11,12,.14)] bg-white px-3.5 py-2 text-[13px] font-medium text-ink"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VIVÊNCIA */}
      <section id="vivencia" className="bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-12 px-6 py-24">
          <div className="flex max-w-[680px] flex-col gap-4">
            <Eyebrow>O QUE VOCÊS VÃO VIVER</Eyebrow>
            <h2 className={H2}>
              Dois dias pensados para o casal, do primeiro ao último momento.
            </h2>
          </div>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            {(
              [
                [
                  <BookOpen key="b" />,
                  'Pregações',
                  'Temas sobre diálogo, perdão, sexualidade e vocação, à luz da Palavra e do ensinamento da Igreja.',
                ],
                [
                  <MessageSquare key="m" />,
                  'Partilhas',
                  'Espaços seguros para conversar a dois e ouvir outros casais que já atravessaram as mesmas lutas.',
                ],
              ] as const
            ).map(([icon, title, text]) => (
              <article
                key={title}
                className="flex flex-col gap-3.5 rounded-[14px] border border-[rgba(11,11,12,.12)] bg-white p-7 transition-colors hover:border-[rgba(11,11,12,.28)]"
              >
                <span className="flex text-brand [&_svg]:size-[26px]">{icon}</span>
                <h3 className="m-0 font-display text-xl font-bold text-ink">{title}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-[#4A4A4F]">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* INSPIRAÇÃO */}
      <section
        id="inspiracao"
        className="border-t border-[rgba(11,11,12,.06)] bg-[#F6F6F4]"
      >
        <div className="mx-auto max-w-[1200px] px-6 py-24">
          <div className="flex max-w-[760px] flex-col gap-5">
            <Eyebrow>NOSSA INSPIRAÇÃO</Eyebrow>
            <h2 className={H2}>
              Luís e Zélia Martin: um casal comum que chegou aos altares.
            </h2>
            <p className={P}>
              Ele relojoeiro, ela rendeira. Pais de nove filhos, entre eles Santa
              Teresinha do Menino Jesus. Viveram o trabalho, as perdas e a criação dos
              filhos com fé, e em 2015 tornaram-se o primeiro casal canonizado junto pela
              Igreja.
            </p>
            <p className={P}>
              A vida deles mostra o que o Enchei-vos deseja para cada casal: que o amor do
              dia a dia seja um verdadeiro caminho de santidade.
            </p>
          </div>
        </div>
      </section>

      {/* PARA QUEM */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1200px] gap-14 px-6 py-24 [grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr))]">
          <div className="flex flex-col gap-4">
            <Eyebrow>PARA QUEM É</Eyebrow>
            <h2 className={H2}>Se vocês se escolheram, este retiro é para vocês.</h2>
            <p className={P}>
              Não é preciso estar em crise, nem ter tudo resolvido. Basta o desejo de
              cuidar do que vocês construíram juntos.
            </p>
          </div>
          <div className="flex flex-col">
            {AUDIENCE.map(([title, text], i) => (
              <div
                key={title}
                className={cn(
                  'flex items-start gap-4 py-5',
                  i < AUDIENCE.length - 1 && 'border-b border-[rgba(11,11,12,.10)]',
                )}
              >
                <CheckCircle2 className="size-[22px] flex-none text-brand" />
                <div className="flex flex-col gap-1">
                  <span className="font-display text-[17px] font-bold text-ink">
                    {title}
                  </span>
                  <span className="text-sm leading-normal text-[#4A4A4F]">{text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section
        id="depoimentos"
        className="border-t border-[rgba(11,11,12,.06)] bg-[#F6F6F4]"
      >
        <div className="mx-auto flex max-w-[1200px] flex-col gap-12 px-6 py-24">
          <div className="flex max-w-[680px] flex-col gap-4">
            <Eyebrow>DEPOIMENTOS</Eyebrow>
            <h2 className={H2}>O que os casais levam para casa.</h2>
          </div>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                className="m-0 flex flex-col justify-between gap-7 rounded-[14px] border border-[rgba(11,11,12,.10)] bg-white p-8"
              >
                <blockquote className="m-0 font-display text-xl leading-[1.4] font-semibold tracking-[-0.01em] text-pretty text-ink">
                  "{t.quote}"
                </blockquote>
                <figcaption className="flex items-center gap-3">
                  <span className="flex size-10 items-center justify-center rounded-full bg-[#FBEDEC] font-display text-sm font-bold text-brand">
                    {t.initials}
                  </span>
                  <span className="text-sm font-semibold text-body">{t.name}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* INSCRIÇÃO */}
      <section id="inscricao" className="bg-white">
        <div className="mx-auto max-w-[1200px] px-6 py-24">
          <div className="grid items-center gap-12 rounded-[24px] border border-[rgba(11,11,12,.12)] p-[clamp(28px,5vw,64px)] [grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr))]">
            <div className="flex flex-col gap-5">
              <Eyebrow>PRÓXIMO RETIRO • {info.monthYear}</Eyebrow>
              <h2 className="m-0 font-display text-[clamp(32px,4vw,52px)] leading-[1.06] font-extrabold tracking-[-0.035em] text-balance text-ink">
                Reservem esse fim de semana um para o outro.
              </h2>
              <p className={P}>
                As vagas são limitadas para que cada casal seja bem acolhido. A inscrição
                leva cerca de 5 minutos.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link className={buttonVariants({ size: 'lg' })} to="/inscricao">
                  Preencher ficha de inscrição
                </Link>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  onClick={() => printFicha(print.open, nextEvent ?? undefined)}
                >
                  Imprimir ficha em papel
                </Button>
              </div>
            </div>
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {(
                [
                  [
                    'Preencham a ficha do casal',
                    'Dados de contato, endereço e um pouco da caminhada de fé de vocês.',
                  ],
                  [
                    'Recebam a confirmação',
                    'A equipe entra em contato pelo telefone informado com as orientações.',
                  ],
                  [
                    'Vivam o Enchei-vos',
                    info.hasEvent
                      ? `${info.days.charAt(0).toUpperCase()}${info.days.slice(1)}, no ${RETREAT_PLACE}.`
                      : `No ${RETREAT_PLACE}.`,
                  ],
                ] as const
              ).map(([title, text], i) => (
                <li
                  key={title}
                  className="flex items-start gap-[18px] rounded-[14px] bg-[#F6F6F4] p-5"
                >
                  <span className="min-w-8 font-display text-[22px] leading-none font-extrabold text-brand">
                    {pad(i + 1)}
                  </span>
                  <div className="flex flex-col gap-1">
                    <span className="font-display text-[17px] font-bold text-ink">
                      {title}
                    </span>
                    <span className="text-sm leading-normal text-[#4A4A4F]">{text}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="duvidas" className="border-t border-[rgba(11,11,12,.06)] bg-[#F6F6F4]">
        <div className="mx-auto grid max-w-[1200px] gap-14 px-6 py-24 [grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr))]">
          <div className="flex flex-col gap-4">
            <Eyebrow>DÚVIDAS FREQUENTES</Eyebrow>
            <h2 className={H2}>Antes de se inscrever.</h2>
            <p className={P}>
              Não encontrou o que procurava? Fale com a equipe da Comunidade Católica
              Apóstolos.
            </p>
          </div>
          <div className="flex flex-col self-start rounded-[14px] border border-[rgba(11,11,12,.10)] bg-white">
            {faq.map(([q, a], i) => {
              const open = openFaq === i;
              return (
                <div
                  key={q}
                  className={cn(
                    i < faq.length - 1 && 'border-b border-[rgba(11,11,12,.08)]',
                  )}
                >
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-[22px] text-left"
                  >
                    <span className="font-display text-[17px] font-bold text-ink">
                      {q}
                    </span>
                    <ChevronDown
                      className={cn(
                        'size-5 flex-none text-brand transition-transform duration-[180ms] ease-[cubic-bezier(.2,.8,.2,1)]',
                        open && 'rotate-180',
                      )}
                    />
                  </button>
                  {open && (
                    <p className="m-0 max-w-[620px] px-6 pb-[22px] text-[15px] leading-[1.65] text-[#4A4A4F]">
                      {a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RODAPÉ */}
      <footer className="border-t border-[rgba(11,11,12,.08)] bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-12 px-6 pt-16 pb-8">
          <div className="grid gap-10 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <img
                  src="/assets/logo.png"
                  alt="Comunidade Católica Apóstolos"
                  className="h-[52px] w-auto brightness-0"
                />
                <span className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-ink">
                  Enchei-vos
                </span>
              </div>
              <p className="m-0 max-w-[300px] text-sm leading-relaxed text-[#4A4A4F]">
                Retiro de Casais da Comunidade Católica Apóstolos.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Label>NAVEGUE</Label>
              {(
                [
                  ['#retiro', 'O Retiro'],
                  ['#inspiracao', 'São Luís e Santa Zélia'],
                  ['#depoimentos', 'Depoimentos'],
                  ['#duvidas', 'Dúvidas frequentes'],
                ] as const
              ).map(([href, label]) => (
                <a key={href} href={href} className="text-sm text-body hover:text-brand">
                  {label}
                </a>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              <Label>LOCAL</Label>
              <span className="text-sm leading-relaxed text-body">
                {RETREAT_PLACE}
                <br />
                Bairro Santo Antônio do Sapezeiro
                <br />
                Santa Bárbara d'Oeste - SP
              </span>
            </div>
          </div>
          <PublicFooterBar className="border-t border-[rgba(11,11,12,.08)] pt-6" />
        </div>
      </footer>

      {print.dialog}
    </div>
  );
};

export default Page;
